// List all env vars from all environments.
// Env vars here that are not specified in BTE/App Configs will be set to null!
const ENV_VARS = {
  // all environments
  NODE_ENV: "${NODE_ENV}",
  APP_ENVIRONMENT: "${APP_ENVIRONMENT}",
  BASE_URL: "${BASE_URL}",
  SEARCH_URL: "${SEARCH_URL}",
  IDENTIFIER_URL: "${IDENTIFIER_URL}",
  ASSET_PATH: "${ASSET_PATH}",
  INIT_URL: "${INIT_URL}",
  REFRESH_TOKEN_URL: "${REFRESH_TOKEN_URL}",
  IFRAME_URL: "${IFRAME_URL}",
};

// Fallback for test environment with sensible defaults
const TEST_ENV_VARS = {
  NODE_ENV: "test",
  APP_ENVIRONMENT: "test",
  BASE_URL: "",
  SEARCH_URL: "/api/search",
  IDENTIFIER_URL: "/api/identifier",
  ASSET_PATH: "/assets",
  INIT_URL: "/api/init",
  REFRESH_TOKEN_URL: "/api/refresh-token",
  // Keep empty so production fallback logic can construct an absolute secure domain URL.
  IFRAME_URL: '',
};

const isUnresolvedTemplate = (value) => {
  return typeof value === 'string' && /^\$\{.+}$/.test(value);
};

const getScriptOrigin = () => {

  if (typeof document === 'undefined') {
    return '';
  }

  const candidates = [];

  if (document.currentScript && document.currentScript.src) {
    candidates.push(document.currentScript.src);
  }

  const scriptElements = document.querySelectorAll('script[src]');
  scriptElements.forEach((script) => {
    if (script && script.src && /benefits-widget|secure-iframe/i.test(script.src)) {
      candidates.push(script.src);
    }
  });

  for (const src of candidates) {
    try {
      return new URL(src).origin;
    } catch (e) {
      // Ignore invalid script URLs and keep looking.
    }
  }

  return '';
};

const getRuntimeOrigin = () => {
  const scriptOrigin = getScriptOrigin();
  if (scriptOrigin) {
    return scriptOrigin;
  }

  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }

  return '';
};

const getRuntimeUrlDefaults = (origin) => ({
  BASE_URL: `${origin}/widgets`,
  SEARCH_URL: `${origin}/services/benefits/searches`,
  IDENTIFIER_URL: `${origin}/services/benefits/card-identifiers`,
  INIT_URL: `${origin}/services/benefits/initialize`,
  REFRESH_TOKEN_URL: `${origin}/services/benefits/access-tokens`,
  IFRAME_URL: `${origin}/services/benefits/iframe.html`,
});

const getOrigin = (urlValue) => {
  try {
    return new URL(urlValue).origin;
  } catch (e) {
    return '';
  }
};

const shouldUseRuntimeOrigin = (normalizedEnvVars, runtimeOrigin) => {
  if (!runtimeOrigin) {
    return false;
  }

  const runtimeHost = (() => {
    try {
      return new URL(runtimeOrigin).hostname;
    } catch (e) {
      return '';
    }
  })();

  if (!runtimeHost || runtimeHost === 'localhost' || runtimeHost === '127.0.0.1') {
    return false;
  }

  const configuredBaseOrigin = getOrigin(normalizedEnvVars.BASE_URL || '');
  // Also use runtime origin when BASE_URL is empty/unresolved (falsy configuredBaseOrigin)
  return !configuredBaseOrigin || configuredBaseOrigin !== runtimeOrigin;
};

const normalizeEnvVars = (rawEnvVars) => {
  const normalized = Object.keys(rawEnvVars).reduce((acc, key) => {
    const value = rawEnvVars[key];
    acc[key] = isUnresolvedTemplate(value) ? (TEST_ENV_VARS[key] || '') : value;
    return acc;
  }, {});

  const runtimeOrigin = getRuntimeOrigin();
  if (shouldUseRuntimeOrigin(normalized, runtimeOrigin)) {
    return {
      ...normalized,
      ...getRuntimeUrlDefaults(runtimeOrigin),
    };
  }

  return normalized;
};

const getEnvVars = () => {
  const envsWhereProcessEnvShouldBeUsed = ["test", "local"];
  const processEnv = typeof process !== "undefined" && process.env ? process.env : null;
  const isJestRun = Boolean(processEnv && processEnv.JEST_WORKER_ID) || (processEnv && processEnv.NODE_ENV === "test");

  if (isJestRun || envsWhereProcessEnvShouldBeUsed.includes((processEnv && processEnv.APP_ENVIRONMENT) || "")) {
    // For test environment, merge with fallbacks to ensure all required properties exist
    return { ...TEST_ENV_VARS, ...(processEnv || {}) };
  }
  return normalizeEnvVars(ENV_VARS);
};

module.exports = {
  getEnvVars,
};

